import React, { useState, useEffect } from 'react';
import JobFlaggingModal from './JobFlaggingModal';
import { sortWorkOrders, formatJobDate, jobServiceId } from '../../utils/jobSort';
import ContractorMetricsCard from './ContractorMetricsCard';
import AdminPaymentsPanel from './AdminPaymentsPanel'; // ✅ NEW
import QuoteRequests from './QuoteRequests'; // ✅ Quote Requests
import WorkOrderReviewModal from './WorkOrderReviewModal';
import WorkOrderScheduleModal from './WorkOrderScheduleModal';
import ServiceWorkPanel from '../work/ServiceWorkPanel';
import { metricTracker } from '../../services/MetricTracker';
import { Link } from 'react-router-dom';
import {
    Users,
    Briefcase,
    Trash2,
    RefreshCw,
    Plus,
    Save,
    AlertTriangle,
    ArrowLeft,
    DollarSign,
    CheckCircle,
    Clock,
    Eye,
    MessageSquare,
    Filter,
    CheckSquare,
    Square,
    Send,
    UserCheck,
    Flag, // ✅ Added for job flagging
    FileText,
    CalendarCheck
} from 'lucide-react';

import NotificationBell from '../NotificationBell';
import InterestedContractorsList from './InterestedContractorsList';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
    collection,
    updateDoc,
    doc,
    deleteDoc,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    writeBatch
} from 'firebase/firestore';

export default function AdminDashboard() {
    const { userProfile, currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('jobs');
    const [reviewJob, setReviewJob] = useState(null);
    const [scheduleJob, setScheduleJob] = useState(null);
    const [executionJob, setExecutionJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showFlaggingModal, setShowFlaggingModal] = useState(false);
    const [selectedJobForFlagging, setSelectedJobForFlagging] = useState(null);
    const [refreshingMetrics, setRefreshingMetrics] = useState(false);

    // Data State
    const [jobs, setJobs] = useState([]);
    const [adminSort, setAdminSort] = useState('createdAt');
    const [adminDirection, setAdminDirection] = useState('desc');
    const [adminSearch, setAdminSearch] = useState('');
    const [contractors, setContractors] = useState([]);
    const [allMessages, setAllMessages] = useState([]);
    const [clientsMap, setClientsMap] = useState({});
    const sortedAdminJobs = sortWorkOrders(jobs.filter(job => {
        const client = clientsMap[job.clientId || job.userId];
        const term = adminSearch.trim().toLowerCase();
        return !term || [jobServiceId(job), job.serviceType, job.title, job.location, job.status, job.clientCompany, job.clientName, job.clientEmail, client?.companyName, client?.name, client?.contactName, client?.email].some(value => String(value || '').toLowerCase().includes(term));
    }), adminSort, adminDirection, {
        clientName: job => { const client = clientsMap[job.clientId || job.userId]; return client?.companyName || job.clientCompany || client?.name || job.clientName || job.clientEmail; },
        contractorName: job => contractors.find(c => c.id === job.contractorId)?.name || null,
    });

    // Selection State for Bulk Delete
    const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());

    // Interested Contractors Modal
    const [showInterestedModal, setShowInterestedModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);

    // Stats State
    const [stats, setStats] = useState({
        revenue: 0,
        completedJobs: 0,
        activeJobs: 0,
        totalContractors: 0,
        unreadMessages: 0
    });

    const [newContractor, setNewContractor] = useState({
        name: '',
        company: '',
        email: '',
        phone: ''
    });

    // REAL-TIME DATA LISTENER
    useEffect(() => {
        if (!userProfile) return;

        setLoading(true);

        // 1. Listen to Jobs
        const unsubJobs = onSnapshot(collection(db, 'workOrders'), (snapshot) => {
            const jobsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setJobs(jobsData);
        }, (error) => console.error("Jobs Listener Error:", error));

        // 2. Listen to Contractors
        const unsubContractors = onSnapshot(collection(db, 'contractors'), (snapshot) => {
            const contractorsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setContractors(contractorsData);
        }, (error) => console.error("Contractors Listener Error:", error));

        // 3. Listen to Users (To map Client Names)
        const unsubUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
            const map = {};
            snapshot.docs.forEach(doc => {
                map[doc.id] = doc.data();
            });
            setClientsMap(map);
        });

        // 4. Listen to Messages (Global Inbox)
        const unsubMessages = onSnapshot(collection(db, 'messages'), (snapshot) => {
            const messagesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            messagesData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setAllMessages(messagesData);
        }, (error) => console.error("Messages Listener Error:", error));

        // Cleanup
        return () => {
            unsubJobs();
            unsubContractors();
            unsubUsers();
            unsubMessages();
        };
    }, [userProfile]);

    // Recalculate stats whenever data updates
    useEffect(() => {
        if (jobs.length > 0 || contractors.length > 0 || allMessages.length > 0) {
            calculateStats(jobs, contractors, allMessages);
            setLoading(false);
        }
    }, [jobs, contractors, allMessages]);

    const calculateStats = (jobsData, contractorsData, messagesData) => {
        let totalRevenue = 0;
        let completedCount = 0;
        let activeCount = 0;

        jobsData.forEach(job => {
            const price = job.totalPrice || 0;
            if (job.status === 'completed') {
                totalRevenue += price;
                completedCount++;
            }
            if (['in_progress', 'in-progress', 'pending_review', 'assigned', 'scheduled'].includes(job.status)) {
                activeCount++;
            }
        });

        const unread = messagesData.filter(m => !m.read && m.senderType === 'client').length;

        setStats({
            revenue: totalRevenue,
            completedJobs: completedCount,
            activeJobs: activeCount,
            totalContractors: contractorsData.length,
            unreadMessages: unread
        });
    };

    const handleDeleteJob = async (jobId) => {
        if (!confirm('Are you sure you want to delete this job?')) return;
        try {
            await deleteDoc(doc(db, 'workOrders', jobId));
        } catch (error) {
            console.error("Error deleting job:", error);
        }
    };

    const handleAddContractor = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, 'contractors'), {
                ...newContractor,
                createdAt: new Date().toISOString()
            });
            setNewContractor({ name: '', company: '', email: '', phone: '' });
        } catch (error) {
            console.error("Error adding contractor:", error);
        }
    };

    const toggleSelectMessage = (messageId) => {
        setSelectedMessageIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(messageId)) {
                newSet.delete(messageId);
            } else {
                newSet.add(messageId);
            }
            return newSet;
        });
    };

    const toggleSelectAllMessages = () => {
        if (selectedMessageIds.size === allMessages.length) {
            setSelectedMessageIds(new Set());
        } else {
            setSelectedMessageIds(new Set(allMessages.map(msg => msg.id)));
        }
    };

    const deleteSelectedMessages = async () => {
        if (!confirm(`Delete ${selectedMessageIds.size} selected messages?`)) return;
        try {
            const batch = writeBatch(db);
            selectedMessageIds.forEach(msgId => {
                batch.delete(doc(db, 'messages', msgId));
            });
            await batch.commit();
            setSelectedMessageIds(new Set());
        } catch (error) {
            console.error("Error deleting messages:", error);
        }
    };

    const handleShowInterested = (job) => {
        setSelectedJob(job);
        setShowInterestedModal(true);
    };

    const handleOpenFlagging = (job) => {
        setSelectedJobForFlagging(job);
        setShowFlaggingModal(true);
    };

    const handleRefreshAllMetrics = async () => {
        setRefreshingMetrics(true);
        try {
            let successCount = 0;
            for (const contractor of contractors) {
                await metricTracker.updateContractorMetrics(contractor.id);
                successCount++;
            }
            alert(`✅ Refreshed metrics for ${successCount} contractors!`);
        } catch (error) {
            console.error('Error refreshing metrics:', error);
            alert('Failed to refresh metrics. Check console for details.');
        } finally {
            setRefreshingMetrics(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                        <p className="text-gray-600 mt-1">Welcome back, {userProfile?.name || 'Admin'}</p>
                    </div>
                    <NotificationBell />
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Revenue</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">${stats.revenue.toFixed(2)}</p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Completed Jobs</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.completedJobs}</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Active Jobs</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.activeJobs}</p>
                            </div>
                            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                                <Clock className="w-6 h-6 text-yellow-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Contractors</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalContractors}</p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <Users className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Unread Messages</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.unreadMessages}</p>
                            </div>
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <MessageSquare className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                    <div className="flex border-b border-gray-200">
                        <button onClick={() => setActiveTab('jobs')} className={`px-6 py-3 font-medium transition-colors ${activeTab === 'jobs' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
                            <div className="flex items-center space-x-2">
                                <Briefcase className="w-5 h-5" />
                                <span>Jobs</span>
                            </div>
                        </button>
                        <button onClick={() => setActiveTab('contractors')} className={`px-6 py-3 font-medium transition-colors ${activeTab === 'contractors' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
                            <div className="flex items-center space-x-2">
                                <Users className="w-5 h-5" />
                                <span>Contractors</span>
                            </div>
                        </button>
                        <button onClick={() => setActiveTab('messages')} className={`px-6 py-3 font-medium transition-colors ${activeTab === 'messages' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
                            <div className="flex items-center space-x-2">
                                <MessageSquare className="w-5 h-5" />
                                <span>Messages</span>
                                {stats.unreadMessages > 0 && (
                                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{stats.unreadMessages}</span>
                                )}
                            </div>
                        </button>
                        {/* ✅ NEW: Payments Tab */}
                        <button onClick={() => setActiveTab('payments')} className={`px-6 py-3 font-medium transition-colors ${activeTab === 'payments' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
                            <div className="flex items-center space-x-2">
                                <DollarSign className="w-5 h-5" />
                                <span>Payments</span>
                            </div>
                        </button>
                        {/* ✅ NEW: Quote Requests Tab */}
                        <button onClick={() => setActiveTab('quotes')} className={`px-6 py-3 font-medium transition-colors ${activeTab === 'quotes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}>
                            <div className="flex items-center space-x-2">
                                <FileText className="w-5 h-5" />
                                <span>Quote Requests</span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div>
                    {/* Jobs Tab */}
                    {activeTab === 'jobs' && (
                        <div className="bg-white rounded-xl shadow-sm overflow-x-auto border border-gray-200">
                            <div className="p-4 border-b border-gray-200 flex flex-wrap items-end gap-3" aria-label="Filter and sort jobs">
                                <label className="text-sm font-semibold text-gray-900">Find a job
                                    <input type="search" value={adminSearch} onChange={event => setAdminSearch(event.target.value)} placeholder="Service ID, company, location, service" className="block mt-1 w-64 max-w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900" />
                                </label>
                                <label className="text-sm font-semibold text-gray-900">Sort by
                                    <select value={adminSort} onChange={event => setAdminSort(event.target.value)} className="block mt-1 px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900">
                                        <option value="createdAt">Request date</option>
                                        <option value="appointment">Appointment date</option>
                                        <option value="serviceId">Service ID</option>
                                        <option value="client">Client</option>
                                        <option value="status">Status</option>
                                        <option value="contractor">Contractor</option>
                                        <option value="service">Service type</option>
                                        <option value="total">Quote amount</option>
                                    </select>
                                </label>
                                <button type="button" onClick={() => setAdminDirection(value => value === 'desc' ? 'asc' : 'desc')} className="min-h-11 px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg font-semibold text-sm">
                                    {adminSort === 'createdAt' || adminSort === 'appointment' ? (adminDirection === 'desc' ? 'Newest first' : 'Oldest first') : adminSort === 'total' ? (adminDirection === 'desc' ? 'Highest first' : 'Lowest first') : (adminDirection === 'asc' ? 'A to Z' : 'Z to A')}
                                </button>
                                <span className="text-sm text-gray-900 ml-auto">{sortedAdminJobs.length} of {jobs.length} jobs</span>
                            </div>
                            <table className="min-w-[1350px] w-full text-left">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-900 uppercase tracking-wider">Service ID</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-900 uppercase tracking-wider">Requested</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Job Details</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contractor</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-900 uppercase tracking-wider">Appointment</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-900 uppercase tracking-wider">Quote</th>
                                        <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {sortedAdminJobs.map((job) => {
                                        const client = clientsMap[job.clientId || job.userId];
                                        const contractor = contractors.find(c => c.id === job.contractorId);
                                        const interestedCount = job.interestedContractors?.length || 0;

                                        return (
                                            <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap font-semibold text-sm text-gray-900">{jobServiceId(job)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatJobDate(job.createdAt)}</td>
                                                <td className="px-6 py-4">
                                                    <div className="font-medium text-gray-900">{job.serviceType}</div>
                                                    <div className="text-sm text-gray-500">{job.location}</div>
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        {typeof job.totalPrice === 'number' ? `$${job.totalPrice.toFixed(2)}` : (job.quote?.totalCents != null ? `Quoted $${(job.quote.totalCents / 100).toFixed(2)}` : 'Awaiting quote')} • {job.createdAt ? formatJobDate(job.createdAt) : 'No date'}
                                                    </div>
                                                    {job.schedule?.date && <div className="text-xs text-green-800 mt-1 font-semibold">Confirmed: {job.schedule.date} · {job.schedule.startTime}–{job.schedule.endTime} CT</div>}
                                                    {job.finalPaymentPaid && <div className="text-xs font-bold text-green-800 mt-1">Paid in Full ✓</div>}
                                                    {!job.finalPaymentPaid && job.finalInvoice?.status === 'due' && <div className="text-xs font-semibold text-orange-800 mt-1">Balance Due: ${(job.finalInvoice.amountCents / 100).toFixed(2)}</div>}
                                                    
                                                    {/* Job Flags Badges */}
                                                    {(job.isSupervised || job.isTraining || job.isExceptional || job.excludeFromMetrics) && (
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {job.isSupervised && (
                                                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                                    Supervised
                                                                </span>
                                                            )}
                                                            {job.isTraining && (
                                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                                                    Training
                                                                </span>
                                                            )}
                                                            {job.isExceptional && (
                                                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                                                    Exceptional
                                                                </span>
                                                            )}
                                                            {job.excludeFromMetrics && (
                                                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                                                    Excluded
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4">
                                                    <div className="font-medium text-gray-900">{client?.name || job.clientName || job.clientEmail || 'Unknown'}</div>
                                                    <div className="text-sm text-gray-500">{client?.email || job.clientEmail || ''}</div>
                                                </td>

                                                <td className="px-6 py-4">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                        job.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                        job.status === 'in_progress' || job.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                                                         job.status === 'pending_review' ? 'bg-amber-100 text-amber-800' :
                                                        job.status === 'scheduled' ? 'bg-green-100 text-green-800' :
                                                        job.status === 'deposit_paid' ? 'bg-emerald-100 text-emerald-800' :
                                                        job.status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                                                        job.status === 'quote_sent' ? 'bg-blue-100 text-blue-800' :
                                                        job.status === 'under_review' || job.status === 'quote_ready' ? 'bg-amber-100 text-amber-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {job.status?.replace(/[_-]/g, ' ') || 'pending'}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-2">
                                                        {contractor ? (
                                                            <div>
                                                                <div className="font-medium text-gray-900">{contractor.name}</div>
                                                                <div className="text-xs text-gray-500">{contractor.company || ''}</div>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleShowInterested(job)}
                                                                disabled={interestedCount === 0 || !job.depositPaid}
                                                                className="inline-flex items-center space-x-1 px-3 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <Send className="w-3 h-3" />
                                                                <span>Push to Pool</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.schedule?.date ? formatJobDate(job.schedule.date) : 'Not scheduled'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Number.isFinite(job.quote?.totalCents) ? `${(job.quote.totalCents / 100).toFixed(2)}` : 'Awaiting quote'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex items-center justify-end space-x-3">
                                                        {/* Flag Button */}
                                                        {job.contractorId && (
                                                            <button
                                                                onClick={() => handleOpenFlagging(job)}
                                                                className="text-purple-600 hover:text-purple-900 p-2 hover:bg-purple-50 rounded-full transition-colors"
                                                                title="Flag Job"
                                                            >
                                                                <Flag className="w-5 h-5" />
                                                            </button>
                                                        )}
                                                        
                                                        {job.depositPaid === true && ['deposit_paid', 'scheduled'].includes(job.status) && (
                                                            <button type="button" onClick={() => setScheduleJob(job)}
                                                                className="inline-flex items-center gap-1 text-white bg-green-700 hover:bg-green-800 px-3 py-2 rounded-lg font-semibold text-xs"
                                                                title="Confirm or change a deposited job's appointment">
                                                                <CalendarCheck size={15} /> {job.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
                                                            </button>
                                                        )}
                                                        {['scheduled', 'in-progress', 'pending_review', 'completed'].includes(job.status) && (
                                                          <button type="button" onClick={() => setExecutionJob(job)}
                                                            className="inline-flex items-center gap-1 text-white bg-indigo-700 hover:bg-indigo-800 px-3 py-2 rounded-lg font-semibold text-xs"
                                                            title="Check in, submit a service report, or review for completion">
                                                            <FileText size={15} /> {job.status === 'pending_review' ? 'Review Report' : 'Work / Report'}
                                                          </button>
                                                        )}
                                                        {/* Review and quote button - admin only */}
                                                        <button
                                                            type="button"
                                                            onClick={() => setReviewJob(job)}
                                                            className="text-indigo-600 hover:text-indigo-900 px-2 py-1 rounded-lg hover:bg-indigo-50 font-semibold text-xs"
                                                            title="Review request and prepare quote"
                                                        >
                                                            Review / Quote
                                                        </button>
                                                        {/* View Button */}
                                                        <Link to={`/jobs/${job.id}`} className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-full transition-colors group relative" title="View Job & Messages">
                                                            <Eye className="w-5 h-5" />
                                                        </Link>
                                                        
                                                        {/* Delete Button */}
                                                        <button onClick={() => handleDeleteJob(job.id)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-full transition-colors" title="Delete Job">
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {sortedAdminJobs.length === 0 && <tr><td colSpan="9" className="px-6 py-12 text-center text-gray-900">No matching jobs.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Contractors Tab */}
                    {activeTab === 'contractors' && (
                        <div className="space-y-6">
                            {/* Refresh Metrics Button */}
                            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
                                <button
                                    onClick={handleRefreshAllMetrics}
                                    disabled={refreshingMetrics}
                                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-4 h-4 ${refreshingMetrics ? 'animate-spin' : ''}`} />
                                    <span>{refreshingMetrics ? 'Refreshing...' : 'Refresh All Metrics'}</span>
                                </button>
                            </div>

                            {/* Contractor Metrics Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {contractors.map((contractor) => (
                                    <ContractorMetricsCard
                                        key={contractor.id}
                                        contractor={contractor}
                                        onUpdate={handleRefreshAllMetrics}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Messages Tab */}
                    {activeTab === 'messages' && (
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                            <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                                <div>
                                    <h3 className="font-semibold text-gray-700">Global Message Inbox</h3>
                                    <p className="text-xs text-gray-500">View recent messages from all jobs</p>
                                </div>
                                {selectedMessageIds.size > 0 && (
                                    <button onClick={deleteSelectedMessages} className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"><Trash2 className="w-4 h-4" /><span>Delete ({selectedMessageIds.size})</span></button>
                                )}
                            </div>
                            <div className="divide-y divide-gray-100">
                                {allMessages.length > 0 && (
                                    <div className="p-2 bg-gray-50 border-b flex items-center">
                                        <button onClick={toggleSelectAllMessages} className="ml-2 p-2 hover:bg-gray-200 rounded-lg flex items-center space-x-2 text-gray-600 text-sm font-medium">
                                            {selectedMessageIds.size === allMessages.length ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5" />}<span>Select All</span>
                                        </button>
                                    </div>
                                )}
                                {allMessages.length > 0 ? (
                                    allMessages.map((msg) => {
                                        const job = jobs.find(j => j.id === msg.workOrderId);
                                        const isSelected = selectedMessageIds.has(msg.id);
                                        const sender = msg.senderName || 'Unknown';

                                        return (
                                            <div key={msg.id} className={`flex hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                                                <div className="p-4 flex items-start"><button onClick={() => toggleSelectMessage(msg.id)}>{isSelected ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}</button></div>
                                                <Link to={`/jobs/${msg.workOrderId}`} className="flex-1 p-4 block">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex items-start space-x-3">
                                                            <div className={`p-2 rounded-full ${msg.senderType === 'client' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}><MessageSquare className="w-5 h-5" /></div>
                                                            <div>
                                                                <div className="flex items-center space-x-2"><span className="font-medium text-gray-900">{sender}</span><span className="text-xs text-gray-400">• {job?.serviceType || 'Unknown Job'}</span>{!msg.read && msg.senderType === 'client' && <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">New</span>}</div>
                                                                <p className="text-gray-600 text-sm mt-1 line-clamp-1">{msg.message}</p>
                                                            </div>
                                                        </div>
                                                        <span className="text-xs text-gray-400 whitespace-nowrap">{msg.createdAt ? new Date(msg.createdAt).toLocaleString() : ''}</span>
                                                    </div>
                                                </Link>
                                            </div>
                                        );
                                    })
                                ) : <div className="p-12 text-center text-gray-500"><MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>No messages found in the system.</p></div>}
                            </div>
                        </div>
                    )}

                    {/* ✅ NEW: Payments Tab */}
                    {activeTab === 'payments' && (
                        <AdminPaymentsPanel />
                    )}

                    {/* ✅ NEW: Quote Requests Tab */}
                    {activeTab === 'quotes' && (
                        <QuoteRequests onReviewJob={job => { setActiveTab('jobs'); setReviewJob(job); }} />
                    )}
                </div>

                {/* Modals */}
                
                {reviewJob && (
                    <WorkOrderReviewModal
                        key={reviewJob.id + ':' + (reviewJob.quote?.version || 0) + ':' + (reviewJob.quote?.status || '')}
                        job={jobs.find(j => j.id === reviewJob.id) || reviewJob}
                        adminId={currentUser?.uid}
                        onClose={() => setReviewJob(null)}
                    />
                )}
                {scheduleJob && (
                    <WorkOrderScheduleModal
                        key={scheduleJob.id}
                        job={jobs.find(j => j.id === scheduleJob.id) || scheduleJob}
                        contractors={contractors}
                        adminId={currentUser?.uid}
                        onClose={() => setScheduleJob(null)}
                    />
                )}
                {executionJob && (
                  <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Service execution and report review">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
                      <div className="flex justify-between items-center gap-4">
                        <h2 className="text-xl font-bold text-gray-900">Service Execution / Report Review</h2>
                        <button type="button" className="border rounded px-3 py-2" onClick={() => setExecutionJob(null)}>Close</button>
                      </div>
                      <ServiceWorkPanel job={jobs.find(j => j.id === executionJob.id) || executionJob} />
                    </div>
                  </div>
                )}
                {/* Interested Contractors Modal */}
                {showInterestedModal && selectedJob && (
                    <InterestedContractorsList
                        job={selectedJob}
                        onClose={() => {
                            setShowInterestedModal(false);
                            setSelectedJob(null);
                        }}
                        onAssign={(contractor) => {
                            console.log('Assigned contractor:', contractor);
                        }}
                    />
                )}

                {/* Job Flagging Modal */}
                {showFlaggingModal && selectedJobForFlagging && (
                    <JobFlaggingModal
                        job={selectedJobForFlagging}
                        onClose={() => {
                            setShowFlaggingModal(false);
                            setSelectedJobForFlagging(null);
                        }}
                        onUpdate={() => {
                            setShowFlaggingModal(false);
                            setSelectedJobForFlagging(null);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
